import { call, put, select } from 'redux-saga/effects';

import { PicApi, UsersApi } from '@shared/api/core-api';
import { IUserDto } from '@shared/api/core-api/users-api';
import { toApiSortDirection } from '@shared/api/core-types';
import { USERS_NUMBER_ON_PAGE } from '@shared/constants';
import { HttpStatusCodes } from '@shared/constants/httpStatusCodes';
import {
  FetchUsersPagePayload,
  RequestByUserIdPayload,
  UserCreatePicPayload,
  UserDeleteConfirmPayload,
  UserDeletePicPayload,
} from '@shared/redux/actions/payloadTypes/userActionsPayloads';
import { ActionWithPayload } from '@shared/redux/actions/typings';
import * as UserActionTypes from '@shared/redux/actions/users-actions';
import { RootState } from '@shared/redux/state/root';
import {
  PicContent,
  UserInChargeEntity,
  UsersFilterState,
  UsersPageDto,
  UsersSortState,
} from '@shared/redux/state/users';
import { downloadFile } from '@shared/util/Downloader';
import { handleSagaError } from '@shared/util/handleSagaError';
import { getPicFileName } from '@shared/util/picFileNameGetter';

export function* fetchPageOfUsers(action: ActionWithPayload<FetchUsersPagePayload>) {
  try {
    const { page } = action.payload;
    const { searchQuery }: UsersFilterState = yield select((state: RootState) => state.users.filter);
    const { field, direction }: UsersSortState = yield select((state: RootState) => state.users.sort);

    const usersPage: UsersPageDto = yield call(
      UsersApi.getPaged,
      searchQuery,
      toApiSortDirection(direction),
      field,
      page,
      USERS_NUMBER_ON_PAGE,
    );

    yield put({
      type: UserActionTypes.FETCH_PAGE_OF_USERS_SUCCESS,
      payload: usersPage,
    });
  } catch (e) {
    yield handleSagaError({
      type: UserActionTypes.FETCH_PAGE_OF_USERS_FAILURE,
      error: e,
    });
  }
}

export function* fetchAllUsersInCharge() {
  try {
    const users: UserInChargeEntity[] = yield call(UsersApi.getUsersInCharge);

    yield put({
      type: UserActionTypes.FETCH_ALL_USER_IN_CHARGE_SUCCESS,
      payload: users,
    });
  } catch (e) {
    yield handleSagaError({
      type: UserActionTypes.FETCH_ALL_USER_IN_CHARGE_FAILURE,
      error: e,
    });
  }
}

export function* fetchCreatePic(action: ActionWithPayload<RequestByUserIdPayload>) {
  try {
    const { userId } = action.payload;
    const pic = (yield call(PicApi.createPickForUser, userId)) as string;

    const createPicAction: ActionWithPayload<UserCreatePicPayload> = {
      type: UserActionTypes.USER_CREATE_PIC_SUCCESS,
      payload: {
        userId,
        pic,
      },
    };
    yield put(createPicAction);
  } catch (e) {
    yield handleSagaError({
      type: UserActionTypes.USER_CREATE_PIC_FAILURE,
      error: e,
    });
  }
}

export function* fetchAccountInfo(): Generator {
  try {
    const accountDetails = (yield call(UsersApi.getMyself)) as IUserDto;

    yield put<UserActionTypes.AccountDetailsRequestSuccessAction>(
      UserActionTypes.accountDetailsRequestSuccess(accountDetails),
    );
  } catch (e) {
    yield handleSagaError(UserActionTypes.accountDetailsRequestFailed(e));
  }
}

export function* fetchPic(action: ActionWithPayload<any>) {
  try {
    const { userId } = action.payload;
    const pic: string | null = yield call(PicApi.getPic, userId);

    if (pic) {
      yield put({
        type: UserActionTypes.USER_PIC_REQUEST_SUCCESS,
        payload: {
          userId,
          picExist: true,
          pic,
        },
      });
    } else {
      yield put({
        type: UserActionTypes.USER_PIC_REQUEST_SUCCESS,
        payload: {
          userId,
          picExist: false,
        },
      });
    }
  } catch (e) {
    yield handleSagaError({
      type: UserActionTypes.USER_PIC_REQUEST_FAILURE,
      error: e,
    });
  }
}

export function* fetchUserById(action: UserActionTypes.FetchUserByIdRequest): Generator {
  try {
    const id = action.payload;
    const user = (yield call(UsersApi.getUserById, id)) as IUserDto;

    yield put(UserActionTypes.fetchUserByIdSuccess(user));
  } catch (e: any) {
    // For user point of view if we get not found and bad request is the same - wrong url entered.
    if (e?.response?.status === HttpStatusCodes.NotFound || e?.response?.status === HttpStatusCodes.BadRequest) {
      yield put(UserActionTypes.fetchUserByIdNotFound(e));
    } else {
      yield handleSagaError(UserActionTypes.fetchUserByIdFailure(e));
    }
  }
}

export function* fetchDeletePic(action: ActionWithPayload<RequestByUserIdPayload>) {
  try {
    const { userId } = action.payload;
    yield call(PicApi.delete, userId);

    const deletePicAction: ActionWithPayload<UserDeletePicPayload> = {
      type: UserActionTypes.USER_DELETE_PIC_SUCCESS,
      payload: { userId },
    };
    yield put(deletePicAction);
  } catch (e) {
    yield handleSagaError({
      type: UserActionTypes.USER_DELETE_PIC_FAILURE,
      error: e,
    });
  }
}

export function* downloadPic(action: ActionWithPayload<PicContent>) {
  try {
    const { pic, firstName, lastName, email } = action.payload;

    const fileName = getPicFileName(firstName, lastName, email);

    yield call(downloadFile, pic, fileName, 'application/octet-stream', true);
    yield put({
      type: UserActionTypes.USER_DOWNLOAD_PIC_SUCCESS,
    });
  } catch (e) {
    yield handleSagaError({
      type: UserActionTypes.USER_DOWNLOAD_PIC_FAILED,
      error: e,
    });
  }
}

export function* fetchDeleteUser(action: ActionWithPayload<UserDeleteConfirmPayload>) {
  try {
    const page: number = yield select((state: RootState) => state.users.pagination.page);
    const { userId } = action.payload;
    yield call(UsersApi.delete, userId);
    yield* fetchPageOfUsers({
      type: UserActionTypes.FETCH_PAGE_OF_USERS_REQUEST,
      payload: { page },
    });
    yield put({
      type: UserActionTypes.USER_DELETE_SUCCESS,
      payload: { userId },
    });
  } catch (e) {
    yield handleSagaError({
      type: UserActionTypes.USER_DELETE_FAILED,
      error: e,
    });
  }
}

export function* setPageNumber(action: ActionWithPayload<number>) {
  try {
    const pageNumber = action.payload;

    yield* fetchPageOfUsers({
      type: UserActionTypes.FETCH_PAGE_OF_USERS_REQUEST,
      payload: { page: pageNumber },
    });
  } catch (e) {
    yield handleSagaError({
      type: UserActionTypes.FETCH_PAGE_OF_USERS_FAILURE,
      error: e,
    });
  }
}

export function* changeSorting(action: UserActionTypes.ChangeUserSorting) {
  const sorting: UsersSortState = yield select((state: RootState) => state.users.sort);

  if (sorting.field != action.payload.field || action.payload.field !== undefined) {
    yield put(UserActionTypes.setPageNumber(0));
  }
}
